import type { AuditInterceptedRequestPayload } from '@/types/auditApiTypes';

function redactAuthorizationHeader(value: string): string {
  return value.toLowerCase().startsWith('bearer ') ? '[REDACTED]' : value;
}

export function normalizeHeadersForAudit(headers: HeadersInit | undefined): Record<string, string> {
  if (!headers) {
    return {};
  }

  if (headers instanceof Headers) {
    const record: Record<string, string> = {};
    headers.forEach((value, key) => {
      record[key] =
        key.toLowerCase() === 'authorization' ? redactAuthorizationHeader(value) : value;
    });
    return record;
  }

  if (Array.isArray(headers)) {
    return Object.fromEntries(
      headers.map(([key, value]) => [
        key,
        key.toLowerCase() === 'authorization' ? redactAuthorizationHeader(value) : value,
      ])
    );
  }

  return Object.fromEntries(
    Object.entries(headers).map(([key, value]) => [
      key,
      key.toLowerCase() === 'authorization' ? redactAuthorizationHeader(String(value)) : String(value),
    ])
  );
}

export async function serializeBodyForAudit(body: BodyInit | null | undefined): Promise<unknown> {
  if (body == null || body === undefined) {
    return null;
  }

  if (typeof body === 'string') {
    try {
      return JSON.parse(body) as unknown;
    } catch {
      return body;
    }
  }

  if (body instanceof FormData) {
    const entries: Record<string, unknown> = {};

    for (const [key, value] of body.entries()) {
      if (value instanceof File) {
        entries[key] = {
          fileName: value.name,
          size: value.size,
          type: value.type,
        };
      } else {
        entries[key] = value;
      }
    }

    return entries;
  }

  if (body instanceof URLSearchParams) {
    return Object.fromEntries(body.entries());
  }

  return '[non-serializable body]';
}

export async function buildAuditInterceptedRequest(
  url: string,
  options: RequestInit
): Promise<AuditInterceptedRequestPayload> {
  const baseUrl = process.env.NEXT_PUBLIC_API_URL ?? '';
  const resolvedUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;

  return {
    method: (options.method ?? 'GET').toUpperCase(),
    url: resolvedUrl,
    headers: normalizeHeadersForAudit(options.headers),
    body: await serializeBodyForAudit(options.body ?? null),
  };
}
